import React, { useCallback } from 'react';
import { BoxProps, ClickAwayListener } from '@mui/material';
import { FocusTrap } from '@mui/base/FocusTrap';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';

import DictionaryHeader from '@replacements/components/ReplacementsDictionary/DictionaryHeader';
import DictionaryBody from '@replacements/components/ReplacementsDictionary/DictionaryBody';
import DictionaryLoading from '@replacements/components/ReplacementsDictionary/DictionaryLoading';
import {
  SuggestionsByRegion,
  useReplacementsDictionaryContext,
} from '@replacements/context/ReplacementsDictionaryContext';
import TabbedReplacementsBody from '@replacements/components/ReplacementsDictionary/TabbedReplacementsBody';
import ReplacementTabProvider from '@replacements/context/replacementsDictionaryTabs/tabsContext';
import { ID_STUDIO_DRAWER_RIGHT } from 'components/Studio/ToolbarDrawer/StudioToolbarWrapper';
import { useCanGenerateLlmReplacements } from 'abilities/llm';

const useStyles = makeStyles()(theme => ({
  modalPaper: {
    height: theme.spacing(59),
    width: theme.spacing(51.25),
    backgroundColor: `${palette.gray_1100} !important`,
    borderRadius: theme.spacing(0.5),
    outline: `1px solid ${palette.gray_800}`,
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
  },
  container: {
    gridTemplateRows: 'auto auto 1fr',
    zIndex: theme.zIndex.drawer,
  },
}));

interface ReplacementsDictionaryProps
  extends Pick<BoxProps, 'className' | 'style'> {
  loading: boolean;
  wordInput?: string;
  replacementInput?: string;
  suggestions: SuggestionsByRegion;
  location: string;
  onClose: () => void;
  ref?: React.RefObject<HTMLDivElement>;
}

const ReplacementsDictionary = React.forwardRef<
  HTMLDivElement,
  ReplacementsDictionaryProps
>((props, ref) => {
  const {
    loading,
    onClose,
    className,
    wordInput,
    suggestions,
    location,
    style,
    replacementInput,
    ...restProps
  } = props;
  const enableLlmIntegration = useCanGenerateLlmReplacements();
  const { showDictionary } = useReplacementsDictionaryContext();
  const { classes, cx } = useStyles();

  if (loading)
    return (
      <FocusTrap open={showDictionary}>
        <div
          ref={ref}
          role="dialog"
          aria-modal="true"
          aria-label="Find Suggestion"
          className={cx(
            classes.container,
            className,
            classes.paper,
            classes.modalPaper
          )}
          style={style}
        >
          <DictionaryLoading
            className={classes.modalPaper}
            {...restProps}
            onClose={onClose}
          />
        </div>
      </FocusTrap>
    );

  return (
    <FocusTrap open={showDictionary}>
      <div
        id="wsl-dictionary-menu"
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label="Find Suggestion"
        className={cx(
          classes.container,
          className,
          classes.paper,
          classes.modalPaper
        )}
        style={style}
        {...restProps}
      >
        {enableLlmIntegration ? (
          <ReplacementTabProvider>
            <TabbedReplacementsBody
              {...props}
              replacementInput={replacementInput}
            />
          </ReplacementTabProvider>
        ) : (
          <>
            <DictionaryHeader onClose={onClose} />
            <DictionaryBody {...props} replacementInput={replacementInput} />
          </>
        )}
      </div>
    </FocusTrap>
  );
});

const ReplacementsDictionaryWrapper = (props: ReplacementsDictionaryProps) => {
  const { reset } = useReplacementsDictionaryContext();
  const handleClickaway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const parent = document.getElementById(ID_STUDIO_DRAWER_RIGHT);
      if (!parent?.contains(event.target as Node)) {
        reset();
      }
    },
    [reset]
  );
  return (
    <ClickAwayListener onClickAway={handleClickaway}>
      <ReplacementsDictionary {...props} />
    </ClickAwayListener>
  );
};

export default ReplacementsDictionaryWrapper;
